import styled from "styled-components";
import { QUERIES, SPACING } from "../../../../../utils/constants";
import Button from "../../../../../components/Button";

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.md}px;
  flex-wrap: wrap;
`;

export const ViewAllLink = styled(Button)`
  @media not all and ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const AddButtonFullLabel = styled.span`
  @media not all and ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const AddButtonShortLabel = styled.span`
  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

const PILL_COLORS: Record<
  string,
  { bg: string; border: string; color: string }
> = {
  EXPENSE: {
    bg: "color-mix(in srgb, var(--color-error) 10%, transparent)",
    border: "color-mix(in srgb, var(--color-error) 35%, transparent)",
    color: "var(--color-error)",
  },
  INCOME: {
    bg: "color-mix(in srgb, var(--color-success) 12%, transparent)",
    border: "color-mix(in srgb, var(--color-success) 40%, transparent)",
    color: "var(--color-success)",
  },
  LOAN: {
    bg: "color-mix(in srgb, #f59e0b 12%, transparent)",
    border: "color-mix(in srgb, #f59e0b 40%, transparent)",
    color: "#b45309",
  },
  TRANSFER: {
    bg: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
    border: "color-mix(in srgb, var(--color-primary) 35%, transparent)",
    color: "var(--color-primary)",
  },
  DEFAULT: {
    bg: "color-mix(in srgb, var(--color-text-secondary) 10%, transparent)",
    border: "color-mix(in srgb, var(--color-text-secondary) 30%, transparent)",
    color: "var(--color-text-secondary)",
  },
};

export const TypePill = styled.span<{ $variant: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${SPACING.xs}px ${SPACING.sm + SPACING.xxs}px;
  border-radius: 999px;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${({ $variant }) =>
    (PILL_COLORS[$variant] ?? PILL_COLORS.DEFAULT).bg};
  border: 1px solid
    ${({ $variant }) => (PILL_COLORS[$variant] ?? PILL_COLORS.DEFAULT).border};
  color: ${({ $variant }) =>
    (PILL_COLORS[$variant] ?? PILL_COLORS.DEFAULT).color};
  white-space: nowrap;
`;

const amountTextColor = (variant: "income" | "expense" | "neutral"): string => {
  if (variant === "income") {
    return "var(--color-success)";
  }
  if (variant === "expense") {
    return "var(--color-error)";
  }
  return "var(--color-text-default)";
};

export const AmountText = styled.span<{
  $variant: "income" | "expense" | "neutral";
}>`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: ${({ $variant }) => amountTextColor($variant)};
`;

/* ---------- Desktop / large-tablet row layout (>= tabletLargeAndUp) ---------- */

export const RowsList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TransactionRow = styled.div`
  display: grid;
  grid-template-columns: 100px 110px 1fr 1.4fr auto auto;
  align-items: center;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px 0;
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--hover-background);
  }
`;

export const DateText = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-weight: 500;
  color: var(--color-text-default);
  white-space: nowrap;
`;

export const CategoryStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  min-width: 0;
`;

export const CategoryName = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-text-default);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SubCategoryName = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const NoteText = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  font-style: italic;
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

export const AmountCell = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const NoteStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xxs}px;
  min-width: 0;
`;

export const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 44px;
`;

/* ---------- Mobile / small-tablet card layout (< tabletLargeAndUp) ---------- */

export const CardsList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TransactionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs + SPACING.xxs}px;
  padding: ${SPACING.md}px 0;
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    border-bottom: none;
  }
`;

export const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const CardTopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  min-width: 0;
`;

export const CardMidRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.sm}px;
`;

export const CardCategory = styled.span`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-md);
  font-weight: 700;
  color: var(--color-text-default);
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

export const CardBottomRow = styled.div`
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardBottomNote = styled.span`
  font-style: italic;
`;

export const CardBottomDot = styled.span`
  margin: 0 ${SPACING.xs + SPACING.xxs}px;
  color: var(--color-text-muted);
`;

/* ---------- Footer "View all" (mobile only) ---------- */

/* ---------- States ---------- */

export const EmptyWrapper = styled.div`
  padding: ${SPACING.md + SPACING.xs}px 0 ${SPACING.xs}px;
  text-align: center;
  font-family: "Crimson Pro", sans-serif;
  font-size: var(--text-base);
  color: var(--color-text-secondary);
`;

export const SkeletonRow = styled.div`
  height: 56px;
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    var(--hover-background) 0%,
    var(--color-card-background) 50%,
    var(--hover-background) 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  margin: ${SPACING.sm}px 0;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
